import { useEffect, useState } from "react";
import { Cheers, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useGet } from "@/hooks/useResource";
import { Loading, PageHeader, StatusBadge } from "@/components/common";
import { DrinksSelection } from "@/components/menu/DrinksSelection";
import { EMPTY_DRINKS, drinksFormFromDoc, drinksPayload } from "@/lib/menuModule";
import { Button } from "@/components/ui/button";

export default function Drinks() {
  const { data, loading, refetch } = useGet("/client/drinks");
  const { data: catalogue } = useGet("/menu-items");
  const [form, setForm] = useState(EMPTY_DRINKS);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (data) {
      setForm(drinksFormFromDoc(data));
    }
  }, [data]);

  const locked = data?.client_status === "confirmed";

  const save = async (clientStatus) => {
    setSaving(clientStatus);
    try {
      await api.put("/client/drinks", { ...drinksPayload(form), client_status: clientStatus });
      toast.success(clientStatus === "confirmed" ? "Drinks confirmed." : "Drinks draft saved.");
      refetch();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return <Loading label="Loading drinks" />;
  }

  return (
    <div data-testid="drinks-page">
      <PageHeader
        eyebrow={
          <span className="inline-flex items-center gap-2" data-testid="drinks-eyebrow">
            <Cheers size={16} weight="light" className="text-accent" aria-hidden="true" />
            Food &amp; Beverage
          </span>
        }
        title="Drinks"
        description="Choose your reception beverage package and tell us the drops you would like. Save a draft while you decide, then confirm."
      >
        <StatusBadge
          status={data?.client_status}
          at={data?.confirmed_at}
          by={data?.confirmed_by}
          testid="drinks-status-badge"
        />
        {!locked && (
          <>
            <Button
              onClick={() => save("draft")}
              disabled={!!saving}
              className="rounded-full bg-primary hover:bg-primary/90"
              data-testid="detail-save-btn"
            >
              <FloppyDisk size={18} className="mr-1" />
              {saving === "draft" ? "Saving…" : "Save draft"}
            </Button>
            <Button
              onClick={() => save("confirmed")}
              disabled={!!saving}
              variant="outline"
              className="rounded-full"
              data-testid="detail-confirm-btn"
            >
              {saving === "confirmed" ? "Confirming…" : "Confirm"}
            </Button>
          </>
        )}
      </PageHeader>
      {locked && (
        <p className="mb-6 text-sm text-muted-foreground" data-testid="drinks-locked-note">
          Your drinks are confirmed. Ask the Events Team if a correction is needed.
        </p>
      )}
      <DrinksSelection catalogue={catalogue} form={form} setForm={setForm} locked={locked} />
    </div>
  );
}
